import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as agencyProviderDeadlineGroupActions from '@redux/modules/agencyProviderDeadlineGroup'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import defaultFormState, { requiredFields } from '@models/agencyProviderDeadlineGroup'
import type { AgencyProviderDeadlineGroupModel, AgencyProviderDeadlineGroupRequestOptions } from '@models/types'

type CreateAgencyProviderDeadlineGroupParams = {
  agencyProviderDeadlineGroupParams: Partial<AgencyProviderDeadlineGroupModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderDeadlineGroupRequestOptions,
}

const createAgencyProviderDeadlineGroup = (params: CreateAgencyProviderDeadlineGroupParams) => {
  const { dispatch, agencyProviderDeadlineGroupParams, requestOptions } = params
  const { createAgencyProviderDeadlineGroup: createFn } = agencyProviderDeadlineGroupActions

  return dispatch(createFn(agencyProviderDeadlineGroupParams, requestOptions))
}

type UpdateAgencyProviderDeadlineGroupParams = {
  agencyProviderDeadlineGroup: AgencyProviderDeadlineGroupModel,
  agencyProviderDeadlineGroupParams: Partial<AgencyProviderDeadlineGroupModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderDeadlineGroupRequestOptions,
}

const updateAgencyProviderDeadlineGroup = (params: UpdateAgencyProviderDeadlineGroupParams) => {
  const {
    dispatch, agencyProviderDeadlineGroup, agencyProviderDeadlineGroupParams, requestOptions,
  } = params
  const { updateAgencyProviderDeadlineGroup: updateFn } = agencyProviderDeadlineGroupActions

  const updatedParams = {
    id: agencyProviderDeadlineGroup.id,
    ...agencyProviderDeadlineGroupParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteAgencyProviderDeadlineGroupParams = {
  agencyProviderDeadlineGroup: DeleteParams<AgencyProviderDeadlineGroupModel>,
  dispatch: AppDispatch,
}

const deleteAgencyProviderDeadlineGroup = (params: DeleteAgencyProviderDeadlineGroupParams) => {
  const { dispatch, agencyProviderDeadlineGroup } = params
  const { deleteAgencyProviderDeadlineGroup: deleteFn } = agencyProviderDeadlineGroupActions

  return dispatch(deleteFn(agencyProviderDeadlineGroup))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAgencyProviderDeadlineGroupForm(
  agencyProviderDeadlineGroup: Partial<AgencyProviderDeadlineGroupModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const agencyProviderDeadlineGroupForm = useForm(
    defaultFormState,
    { entity: agencyProviderDeadlineGroup, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [agencyProviderDeadlineGroup.id, agencyProviderDeadlineGroup.cache_key],
  )

  return {
    ...agencyProviderDeadlineGroupForm,
  }
}

export const useRelations = (agencyProviderDeadlineGroup: Partial<AgencyProviderDeadlineGroupModel> = {}) => {
  const { provider_id, provider_deadline_group_id } = agencyProviderDeadlineGroup

  const { providers, providerDeadlineGroups } = useSelector(reduxState => reduxState.entities)

  const provider = provider_id ? providers[provider_id] || {} : {}
  const providerDeadlineGroup = provider_deadline_group_id
    ? providerDeadlineGroups[provider_deadline_group_id] || {}
    : {}
  return {
    provider,
    providerDeadlineGroup,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: AgencyProviderDeadlineGroupRequestOptions,
}

function useAgencyProviderDeadlineGroup(
  initEntity: Partial<AgencyProviderDeadlineGroupModel> = {},
  options: Options = {},
) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: agencyProviderDeadlineGroup }: { entity: AgencyProviderDeadlineGroupModel } = useLatestEntity(initEntity, 'agencyProviderDeadlineGroups')
  const { id } = agencyProviderDeadlineGroup

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  useReduxAction(
    'agencyProviderDeadlineGroups',
    'loadAgencyProviderDeadlineGroup',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.agencyProviderDeadlineGroups)

  const {
    provider,
    providerDeadlineGroup,
  } = useRelations(agencyProviderDeadlineGroup)

  return {
    agencyProviderDeadlineGroup,
    callbacks: {
      createAgencyProviderDeadlineGroup:
      (
        agencyProviderDeadlineGroupParams: Partial<AgencyProviderDeadlineGroupModel>,
        entityOptions?: AgencyProviderDeadlineGroupRequestOptions,
      ) => (
        createAgencyProviderDeadlineGroup({
          agencyProviderDeadlineGroupParams,
          dispatch,
          requestOptions: entityOptions,
        })
      ),
      createOrEditAgencyProviderDeadlineGroup: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAgencyProviderDeadlineGroupModal',
        payload: { agencyProviderDeadlineGroup },
      }),
      deleteAgencyProviderDeadlineGroup: () => deleteAgencyProviderDeadlineGroup({
        agencyProviderDeadlineGroup,
        dispatch,
      }),
      updateAgencyProviderDeadlineGroup: (
        agencyProviderDeadlineGroupParams: Partial<AgencyProviderDeadlineGroupModel>,
        entityOptions?: AgencyProviderDeadlineGroupRequestOptions,
      ) => (
        updateAgencyProviderDeadlineGroup({
          agencyProviderDeadlineGroup, agencyProviderDeadlineGroupParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading,
    provider,
    providerDeadlineGroup,
    updating,
  }
}

export default useAgencyProviderDeadlineGroup
